export const REGION_GROUPS = {
  CIRCUMFERENCE: "circumference",
};

/**
 * persistAs:
 *  - circumference: maps to circumference.<apiField>
 *  - anthropometry: maps to anthropometry.<apiField>
 *  - custom: maps to custom_circumferences[{ label, value_cm }]
 *  - calculated: read-only display, not sent as input
 */
export const CIRCUMFERENCE_CATALOG = [
  // Circunferências corporais (cm)
  {
    key: "waist",
    label: "Circunferência da cintura (CC)",
    shortLabel: "CC",
    hubLabel: "Cintura",
    group: REGION_GROUPS.CIRCUMFERENCE,
    unit: "cm",
    persistAs: "circumference",
    apiField: "waist",
    description: "Avalia adiposidade abdominal e risco cardiovascular.",
  },
  {
    key: "hips",
    label: "Circunferência do quadril (CQ)",
    shortLabel: "CQ",
    hubLabel: "Quadril",
    group: REGION_GROUPS.CIRCUMFERENCE,
    unit: "cm",
    persistAs: "circumference",
    apiField: "hips",
  },
  {
    key: "abdominal_circ",
    label: "Circunferência abdominal (CA)",
    shortLabel: "CA",
    hubLabel: "Abdômen",
    group: REGION_GROUPS.CIRCUMFERENCE,
    unit: "cm",
    persistAs: "custom",
  },
  {
    key: "neck",
    label: "Circunferência do pescoço (CP)",
    shortLabel: "CP",
    hubLabel: "Pescoço",
    group: REGION_GROUPS.CIRCUMFERENCE,
    unit: "cm",
    persistAs: "circumference",
    apiField: "neck",
  },
  {
    key: "biceps",
    label: "Circunferência do braço (CB)",
    shortLabel: "CB",
    hubLabel: "Bíceps",
    group: REGION_GROUPS.CIRCUMFERENCE,
    unit: "cm",
    persistAs: "circumference",
    apiField: "biceps",
  },
  {
    key: "cmb",
    label: "Circunferência muscular do braço (CMB)",
    shortLabel: "CMB",
    hubLabel: "CMB",
    group: REGION_GROUPS.CIRCUMFERENCE,
    unit: "cm",
    persistAs: "custom",
  },
  {
    key: "calf",
    label: "Circunferência da panturrilha (CPant)",
    shortLabel: "CPant",
    hubLabel: "Panturrilha",
    group: REGION_GROUPS.CIRCUMFERENCE,
    unit: "cm",
    persistAs: "circumference",
    apiField: "calf",
  },
  {
    key: "thigh",
    label: "Circunferência da coxa",
    shortLabel: "Coxa",
    hubLabel: "Coxa",
    group: REGION_GROUPS.CIRCUMFERENCE,
    unit: "cm",
    persistAs: "circumference",
    apiField: "thigh",
  },
  {
    key: "wrist",
    label: "Circunferência do punho",
    shortLabel: "Punho",
    hubLabel: "Punho",
    group: REGION_GROUPS.CIRCUMFERENCE,
    unit: "cm",
    persistAs: "circumference",
    apiField: "wrist",
  },
  {
    key: "chest",
    label: "Circunferência do tórax",
    shortLabel: "Tórax",
    hubLabel: "Tórax",
    group: REGION_GROUPS.CIRCUMFERENCE,
    unit: "cm",
    persistAs: "circumference",
    apiField: "chest",
  },

];

const normalizeLabel = (label) =>
  (label || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

export const getCatalogByKey = (key) =>
  CIRCUMFERENCE_CATALOG.find((item) => item.key === key);

export const matchCatalogByLabel = (label) => {
  const normalized = normalizeLabel(label);
  return CIRCUMFERENCE_CATALOG.find(
    (item) => normalizeLabel(item.label) === normalized
  );
};

/** Only selectable (input) catalog items — excludes calculated ratios */
export const getSelectableCatalog = (group = null) =>
  CIRCUMFERENCE_CATALOG.filter((item) => {
    if (item.persistAs === "calculated") {
      return false;
    }
    if (group && item.group !== group) {
      return false;
    }
    return true;
  });

export const getAvailableRegions = (selectedCards = [], group = null) => {
  const usedKeys = new Set(selectedCards.map((card) => card.key));
  const usedLabels = new Set(
    selectedCards.map((card) => normalizeLabel(card.label))
  );

  return getSelectableCatalog(group).filter(
    (item) =>
      !usedKeys.has(item.key) && !usedLabels.has(normalizeLabel(item.label))
  );
};

export const createCardFromCatalog = (catalogItem, value = "") => ({
  key: catalogItem.key,
  label: catalogItem.label,
  shortLabel: catalogItem.shortLabel || catalogItem.label,
  hubLabel: catalogItem.hubLabel || null,
  group: catalogItem.group,
  unit: catalogItem.unit || "cm",
  persistAs: catalogItem.persistAs || "custom",
  apiField: catalogItem.apiField || null,
  description: catalogItem.description || null,
  value: value === null || value === undefined ? "" : String(value),
});

export const DEFAULT_STARTER_KEYS = ["waist", "hips", "biceps", "calf"];

export const FORM_LAYOUT_LABEL_PREFIX = "__lorie_circumference_form__:";

export const isFormLayoutEntry = (item) =>
  Boolean(item?.label?.startsWith(FORM_LAYOUT_LABEL_PREFIX));

export const buildFormLayoutLabel = (selectedCards = []) =>
  `${FORM_LAYOUT_LABEL_PREFIX}${selectedCards.map((card) => card.key).join("|")}`;

export const parseFormLayoutKeys = (measurement) => {
  const entry = (measurement?.custom_circumferences || []).find(isFormLayoutEntry);
  if (!entry?.label) {
    return null;
  }

  const keysPart = entry.label.slice(FORM_LAYOUT_LABEL_PREFIX.length);
  if (!keysPart) {
    return null;
  }

  return keysPart.split("|").filter(Boolean);
};

export const getStarterCards = () =>
  DEFAULT_STARTER_KEYS.map((key) => {
    const catalogItem = getCatalogByKey(key);
    return catalogItem ? createCardFromCatalog(catalogItem, "") : null;
  }).filter(Boolean);

export const createLegacyCard = (label, value = "") => ({
  key: `legacy-${normalizeLabel(label).replace(/\s+/g, "-")}`,
  label,
  shortLabel: label,
  group: REGION_GROUPS.CIRCUMFERENCE,
  unit: "cm",
  persistAs: "custom",
  apiField: null,
  description: null,
  value: value === null || value === undefined ? "" : String(value),
});

const appendUniqueCard = (cards, catalogItem, value) => {
  if (value === null || value === undefined) {
    return;
  }
  if (cards.some((card) => card.key === catalogItem.key)) {
    return;
  }
  cards.push(createCardFromCatalog(catalogItem, value));
};

export const cardsFromMeasurement = (measurement, group = null) => {
  if (!measurement) {
    return [];
  }

  const cards = [];
  const circumference = measurement.circumference || {};
  const anthropometry = measurement.anthropometry || {};

  CIRCUMFERENCE_CATALOG.forEach((item) => {
    if (group && item.group !== group) {
      return;
    }
    if (item.persistAs === "circumference" && item.apiField) {
      appendUniqueCard(cards, item, circumference[item.apiField]);
    }
    if (item.persistAs === "anthropometry" && item.apiField) {
      appendUniqueCard(cards, item, anthropometry[item.apiField]);
    }
  });

  (measurement.custom_circumferences || []).forEach((item) => {
    if (!item?.label || isFormLayoutEntry(item)) {
      return;
    }
    const catalogItem = matchCatalogByLabel(item.label);
    if (catalogItem) {
      if (group && catalogItem.group !== group) {
        return;
      }
      appendUniqueCard(cards, catalogItem, item.value_cm);
      return;
    }
    if (!group || group === REGION_GROUPS.CIRCUMFERENCE) {
      cards.push(createLegacyCard(item.label, item.value_cm));
    }
  });

  return cards;
};

export const cardsFromLayoutKeys = (keys = [], measurement = null) => {
  const valueCards = cardsFromMeasurement(measurement, REGION_GROUPS.CIRCUMFERENCE);
  const valueByKey = new Map(valueCards.map((card) => [card.key, card.value]));

  return keys
    .map((key) => {
      const catalogItem = getCatalogByKey(key);
      if (!catalogItem) {
        return null;
      }
      return createCardFromCatalog(catalogItem, valueByKey.get(key) ?? "");
    })
    .filter(Boolean);
};

export const measurementHasCircumference = (measurement) => {
  if (!measurement) {
    return false;
  }
  return cardsFromMeasurement(measurement, REGION_GROUPS.CIRCUMFERENCE).some(
    (card) => card.value !== "" && card.value != null && card.value !== undefined
  );
};

export const resolveCardsForForm = (measurement) => {
  const layoutKeys = parseFormLayoutKeys(measurement);
  if (layoutKeys?.length) {
    return cardsFromLayoutKeys(layoutKeys, measurement);
  }

  const fromHistory = cardsFromMeasurement(measurement, REGION_GROUPS.CIRCUMFERENCE);
  if (fromHistory.length > 0) {
    return fromHistory;
  }

  return getStarterCards();
};

export const AUTOMATIC_CALCULATION_ITEMS = [
  {
    key: "imc",
    label: "Índice de massa corporal",
    source: "anthropometry",
    field: "bmi",
  },
  {
    key: "iac",
    label: "Índice de adiposidade corporal",
    source: "anthropometry",
    field: "body_adiposity_index",
  },
  {
    key: "rcq",
    label: "Relação cintura-quadril",
    source: "circumference",
    field: "waist_to_hip_ratio",
  },
  {
    key: "rcest",
    label: "Relação cintura-estatura",
    source: "circumference",
    field: "waist_to_height_ratio",
  },
];

export const buildAutomaticCalculationItems = (measurement) => {
  const circumference = measurement?.circumference || {};
  const anthropometry = measurement?.anthropometry || {};

  return AUTOMATIC_CALCULATION_ITEMS.map((item) => {
    const sourceData = item.source === "circumference" ? circumference : anthropometry;
    const raw = sourceData[item.field] ?? null;

    return {
      key: item.key,
      label: item.label,
      value: formatCircumferenceValue(raw),
    };
  });
};

const buildMetabolicData = ({ patient, weightKg, heightCm } = {}) => {
  const resolvedHeight =
    heightCm !== "" && heightCm != null && heightCm !== undefined
      ? Number(heightCm)
      : patient?.body_measurement?.height != null
        ? Number(patient.body_measurement.height)
        : null;
  const resolvedWeight =
    weightKg !== "" && weightKg != null && weightKg !== undefined
      ? Number(weightKg)
      : patient?.body_measurement?.weight != null
        ? Number(patient.body_measurement.weight)
        : null;

  if (
    resolvedHeight == null ||
    Number.isNaN(resolvedHeight) ||
    resolvedWeight == null ||
    Number.isNaN(resolvedWeight)
  ) {
    return null;
  }

  return {
    height_cm: resolvedHeight,
    weight_kg: resolvedWeight,
  };
};

export const buildSavePayload = ({
  nutritionistId,
  patientId,
  visitDate,
  selectedCards,
  patient,
  weightKg,
  heightCm,
  measurementId,
}) => {
  const circumference = {};
  const anthropometry = {};
  const customCircumferences = [];

  selectedCards.forEach((card) => {
    if (card.value === "" || card.value === null || card.value === undefined) {
      return;
    }

    const numericValue = Number(card.value);
    if (Number.isNaN(numericValue)) {
      return;
    }

    if (card.persistAs === "circumference" && card.apiField) {
      circumference[card.apiField] = numericValue;
      return;
    }

    if (card.persistAs === "anthropometry" && card.apiField) {
      anthropometry[card.apiField] = numericValue;
      return;
    }

    customCircumferences.push({
      label: card.label.trim(),
      value_cm: numericValue,
    });
  });

  if (selectedCards.length > 0) {
    customCircumferences.push({
      label: buildFormLayoutLabel(selectedCards),
      value_cm: 0,
    });
  }

  const metabolicData = buildMetabolicData({ patient, weightKg, heightCm });

  const payload = {
    nutritionist_id: nutritionistId,
    patient_id: Number(patientId),
    visit_date: visitDate,
    ...(Object.keys(circumference).length > 0 ? { circumference } : {}),
    ...(Object.keys(anthropometry).length > 0 ? { anthropometry } : {}),
    ...(customCircumferences.length > 0
      ? { custom_circumferences: customCircumferences }
      : {}),
    ...(metabolicData ? { metabolic_data: metabolicData } : {}),
  };

  if (measurementId != null && measurementId !== "") {
    payload.id = Number(measurementId);
  }

  return payload;
};

export const buildCircumferenceDiscardPayload = ({
  nutritionistId,
  patientId,
  patient,
  weightKg,
  heightCm,
  measurementId,
  initialSnapshot,
}) => {
  const parsed = JSON.parse(initialSnapshot);
  const payload = buildSavePayload({
    nutritionistId,
    patientId,
    visitDate: toApiDate(parsed.visitDate),
    selectedCards: parsed.selectedCards,
    patient,
    weightKg: parsed.weightKg ?? weightKg,
    heightCm: parsed.heightCm ?? heightCm,
    measurementId,
  });

  const circumference = {};
  CIRCUMFERENCE_CATALOG.forEach((item) => {
    if (item.persistAs !== "circumference" || !item.apiField) {
      return;
    }

    const card = parsed.selectedCards.find((entry) => entry.key === item.key);
    const rawValue = card?.value;
    if (rawValue !== "" && rawValue != null && rawValue !== undefined) {
      const numericValue = Number(rawValue);
      if (!Number.isNaN(numericValue)) {
        circumference[item.apiField] = numericValue;
        return;
      }
    }

    circumference[item.apiField] = null;
  });

  payload.circumference = circumference;
  payload.replace_circumference = true;
  return payload;
};

/** Minimal history point (visit date + optional metabolic data) for hub "Salvar" in new mode */
export const buildMinimalHistoryPayload = ({
  nutritionistId,
  patientId,
  visitDate,
  patient,
  weightKg,
  heightCm,
  measurementId,
}) => {
  const metabolicData = buildMetabolicData({ patient, weightKg, heightCm });

  const payload = {
    nutritionist_id: nutritionistId,
    patient_id: Number(patientId),
    visit_date: visitDate,
    ...(metabolicData ? { metabolic_data: metabolicData } : {}),
  };

  if (measurementId != null && measurementId !== "") {
    payload.id = Number(measurementId);
  }

  return payload;
};

export const formatCircumferenceValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return Number(value).toLocaleString("pt-BR", {
    maximumFractionDigits: 2,
  });
};

const circumferenceDisplayLabel = (label) =>
  (label || "").replace(/\s*\([^)]+\)\s*$/, "").trim() || label;

const resolveCircumferenceHubLabel = (card) => {
  if (card.hubLabel) {
    return card.hubLabel;
  }

  const stripped = circumferenceDisplayLabel(card.label)
    .replace(/^Circunferência\s+(da|do|de|dos|das)\s+/i, "")
    .trim();

  if (stripped) {
    return stripped.charAt(0).toUpperCase() + stripped.slice(1);
  }

  return card.label;
};

export const buildCircumferenceItems = (measurement) => {
  if (!measurement) {
    return [];
  }

  return cardsFromMeasurement(measurement, REGION_GROUPS.CIRCUMFERENCE).map((card) => {
    const hubLabel = resolveCircumferenceHubLabel(card);

    return {
      key: card.key,
      title: hubLabel,
      fullTitle: circumferenceDisplayLabel(card.label),
      value: formatCircumferenceValue(card.value),
      unit: card.unit,
    };
  });
};

export const toApiDate = (displayDate) => {
  if (!displayDate) {
    return null;
  }

  if (displayDate.includes("/") && displayDate.length === 10) {
    return displayDate.split("/").reverse().join("-");
  }

  const datePart = String(displayDate).split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return datePart;
  }

  return null;
};

export const todayDisplayDate = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const year = today.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatVisitDateLabel = (visitDate) => {
  if (!visitDate) {
    return "";
  }

  if (visitDate.includes("/")) {
    return visitDate.length >= 10 ? visitDate.slice(0, 10) : visitDate;
  }

  const datePart = String(visitDate).split("T")[0];
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) {
    return visitDate;
  }

  return `${day.slice(0, 2)}/${month}/${year}`;
};
